import {
  Badge,
  Check,
  Close,
  Edit,
  Euro,
  MarkEmailRead,
  NotificationsActive,
  OpenInNew,
} from '@mui/icons-material'
import { Box, IconButton, Link as MuiLink, Stack, TextField, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import React from 'react'

import { useUpdateAppointmentMutation } from '../../api'
import type { Appointment } from '../../types.ts'
import Header from '../Header'

import { formatCurrency, getFormattedDateAndTime } from '@/shared/utils'

interface Props {
  appointment: Appointment
  onRefetch?: () => void
}

export const SectionAppointment = ({ appointment, onRefetch }: Props) => {
  const queryClient = useQueryClient()

  const [isEditingCostPerOSA, setIsEditingCostPerOSA] = React.useState(false)
  const [costPerOSAValue, setCostPerOSAValue] = React.useState<string>('')

  const updateAppointmentMutation = useUpdateAppointmentMutation({
    onSuccess: () => {
      setIsEditingCostPerOSA(false)
      // Invalidate all appointment-related queries to trigger refetch
      queryClient.invalidateQueries({
        predicate: query => {
          const key = query.queryKey
          return (
            (Array.isArray(key) && key.includes('appointments')) ||
            (Array.isArray(key) && key.includes('default'))
          )
        },
      })
      // Trigger parent refetch if callback is provided
      if (onRefetch) {
        onRefetch()
      }
    },
  })

  const handleEditCostPerOSA = () => {
    setCostPerOSAValue(appointment?.costPerOSA?.toString() || '')
    setIsEditingCostPerOSA(true)
  }

  const handleSaveCostPerOSA = () => {
    if (!appointment?.uuid) return

    const numericValue = costPerOSAValue === '' ? null : parseFloat(costPerOSAValue)

    updateAppointmentMutation.mutate({
      id: appointment.uuid,
      data: {
        costPerOSA: numericValue,
        status: appointment.status,
        buildingUUID: appointment.buildingUUID,
        scoutUUID: appointment.scoutUUID,
        scheduleStartTime: appointment.scheduleStartTime,
      },
    })
  }

  const handleCancelCostPerOSA = () => {
    setIsEditingCostPerOSA(false)
    setCostPerOSAValue('')
  }
  return (
    <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 45%' } }}>
      <Header label="Appointment Information" />
      <Stack spacing={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Badge color="action" />
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Appointment ID
            </Typography>
            <Typography variant="body2">{appointment.uuid}</Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <NotificationsActive color="action" />
          <Box>
            <Typography variant="subtitle2" color="text.secondary">
              Scout Notification Sent
            </Typography>
            <Typography
              variant="body2"
              color={!appointment.scoutNotificationSent ? 'text.secondary' : 'text.primary'}
            >
              {appointment.scoutNotificationSent
                ? getFormattedDateAndTime(appointment.scoutNotificationSent)
                : 'Not sent'}
            </Typography>
          </Box>
        </Box>
        {appointment.confirmationEmailSent && (
          <Box display="flex" alignItems="center" gap={1}>
            <MarkEmailRead color="action" />
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Confirmation Email Sent
              </Typography>
              <Typography variant="body2" color="text.primary">
                {getFormattedDateAndTime(appointment.confirmationEmailSent)}
              </Typography>
            </Box>
          </Box>
        )}
        {appointment.reminderEmailSent && (
          <Box display="flex" alignItems="center" gap={1}>
            <MarkEmailRead color="action" />
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Reminder Email Sent
              </Typography>
              <Typography variant="body2" color="text.primary">
                {getFormattedDateAndTime(appointment.reminderEmailSent)}
              </Typography>
            </Box>
          </Box>
        )}
        <Box display="flex" alignItems="center" gap={1}>
          <Euro color="action" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Cost per OSA
            </Typography>
            {isEditingCostPerOSA ? (
              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                <TextField
                  size="small"
                  type="number"
                  value={costPerOSAValue}
                  onChange={e => setCostPerOSAValue(e.target.value)}
                  placeholder="Enter amount"
                  inputProps={{
                    step: '0.01',
                    min: '0',
                  }}
                  sx={{ width: '150px' }}
                  autoFocus
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleSaveCostPerOSA()
                    } else if (e.key === 'Escape') {
                      handleCancelCostPerOSA()
                    }
                  }}
                />
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleSaveCostPerOSA}
                  disabled={updateAppointmentMutation.isPending}
                >
                  <Check fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={handleCancelCostPerOSA}
                  disabled={updateAppointmentMutation.isPending}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box display="flex" alignItems="center" gap={1}>
                <Typography
                  variant="body2"
                  color={appointment.costPerOSA == null ? 'text.secondary' : 'text.primary'}
                >
                  {appointment.costPerOSA != null
                    ? formatCurrency(Number(appointment.costPerOSA), 'EUR')
                    : 'Not specified'}
                </Typography>
                <IconButton size="small" onClick={handleEditCostPerOSA} sx={{ ml: 0.5 }}>
                  <Edit fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>
        {appointment.bauhowLink && (
          <Box display="flex" alignItems="center" gap={1}>
            <OpenInNew color="action" />
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Bauhow Link
              </Typography>
              <MuiLink
                href={appointment.bauhowLink}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
              >
                Open in Bauhow
              </MuiLink>
            </Box>
          </Box>
        )}
      </Stack>
    </Box>
  )
}

export default SectionAppointment
